import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, Calendar, X, Download, Trash2, AlertTriangle, Send, MessageSquare, CheckCircle2, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const COLORS = [
  { key: 'green', label: 'Entró a clase', css: 'rgba(34,197,94,0.55)', dot: '#16a34a' },
  { key: 'amber', label: 'Entró tarde / sacó a los alumnos antes', css: 'rgba(250,204,21,0.65)', dot: '#facc15' },
  { key: 'red', label: 'No se presentó', css: 'rgba(239,68,68,0.55)', dot: '#dc2626' },
  { key: 'na', label: 'Justificado (reunión / comisión)', css: 'rgba(148,163,184,0.55)', dot: '#64748b' },
];
const colorCss = (k) => (COLORS.find(c => c.key === k) || {}).css || 'transparent';

const HorariosPage = ({ user, embedded }) => {
  const role = user.role;
  const canUpload = role === 'jefe_docentes';
  const canMark = role === 'prefectura';

  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState('');
  const [sched, setSched] = useState(null);
  const [marks, setMarks] = useState({}); // key `r-c` -> color
  const [weeks, setWeeks] = useState([]);
  const [weekStart, setWeekStart] = useState(null);
  const [selCell, setSelCell] = useState(null); // {row,col}
  const [rows, setRows] = useState(8);
  const [cols, setCols] = useState(5);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [markedBy, setMarkedBy] = useState({}); // key `r-c` -> prefecto id
  const [prefectos, setPrefectos] = useState([]);
  const [noticeCell, setNoticeCell] = useState(null); // {row,col}
  const [noticeTo, setNoticeTo] = useState('');
  const [noticeMsg, setNoticeMsg] = useState('');
  const [sendingNotice, setSendingNotice] = useState(false);
  const [sentNotices, setSentNotices] = useState([]);
  const [showSent, setShowSent] = useState(false);
  const exportRef = useRef(null);
  const paletteRef = useRef(null);
  const noticeRef = useRef(null);

  const canSend = role === 'subdirector' || role === 'jefe_docentes';

  useEffect(() => {
    if (selCell && paletteRef.current) {
      paletteRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selCell]);

  useEffect(() => {
    if (noticeCell && noticeRef.current) {
      noticeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [noticeCell]);

  const loadSentNotices = useCallback(() => {
    axios.get(`${API}/schedule-notices`, authHeaders())
      .then(r => setSentNotices(r.data.notices || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!canSend) return;
    axios.get(`${API}/schedule-prefectos`, authHeaders()).then(r => setPrefectos(r.data || [])).catch(() => {});
    loadSentNotices();
  }, [canSend, loadSentNotices]);

  const canExport = role === 'subdirector' || role === 'jefe_docentes';
  const groupName = (groups.find(g => g.id === groupId) || {}).name || '';

  // Columna del día de hoy (0=Lun … 4=Vie). Fin de semana => -1 (sin resaltar).
  const todayCol = useMemo(() => {
    const wd = (new Date().getDay() + 6) % 7; // 0=Lun … 6=Dom
    return wd >= 5 ? -1 : wd;
  }, []);
  const isCurrentWeek = !!weekStart && !!sched?.current_week_start && weekStart === sched.current_week_start;
  const showToday = isCurrentWeek && todayCol >= 0 && todayCol < cols;

  useEffect(() => {
    axios.get(`${API}/schedules/groups`, authHeaders())
      .then(r => setGroups(r.data || []))
      .catch(() => toast.error('No se pudieron cargar los grupos'));
  }, []);

  const loadMarks = useCallback((gid, ws) => {
    const q = ws ? `?week_start=${ws}` : '';
    axios.get(`${API}/schedules/${gid}/marks${q}`, authHeaders())
      .then(r => {
        const m = {}; const mb = {};
        (r.data.marks || []).forEach(x => {
          m[`${x.row}-${x.col}`] = x.color;
          if (x.marked_by) mb[`${x.row}-${x.col}`] = x.marked_by;
        });
        setMarks(m);
        setMarkedBy(mb);
        setWeekStart(r.data.week_start);
      })
      .catch(() => { setMarks({}); setMarkedBy({}); });
  }, []);

  const selectGroup = useCallback((gid) => {
    setGroupId(gid); setSched(null); setMarks({}); setMarkedBy({}); setSelCell(null); setNoticeCell(null); setWeeks([]); setWeekStart(null); setConfirmDelete(false);
    if (!gid) return;
    axios.get(`${API}/schedules/${gid}`, authHeaders())
      .then(r => { setSched(r.data); if (r.data.grid_rows) { setRows(r.data.grid_rows); setCols(r.data.grid_cols); } })
      .catch(() => setSched({ has_image: false }));
    loadMarks(gid, null);
    if (!canMark) {
      axios.get(`${API}/schedules/${gid}/weeks`, authHeaders()).then(r => setWeeks(r.data.weeks || [])).catch(() => {});
    }
  }, [loadMarks, canMark]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file); fd.append('grid_rows', rows); fd.append('grid_cols', cols);
    try {
      await axios.post(`${API}/schedules/${groupId}/upload`, fd, { headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' } });
      toast.success('Horario subido');
      selectGroup(groupId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const applyColor = async (color) => {
    if (!canMark || !selCell) return;
    const { row, col } = selCell;
    const key = `${row}-${col}`;
    const prev = marks[key];
    const next = { ...marks };
    if (color) next[key] = color; else delete next[key];
    setMarks(next); setSelCell(null);
    try {
      await axios.put(`${API}/schedules/${groupId}/mark`, { row, col, color: color || null, week_start: weekStart }, authHeaders());
    } catch (err) {
      setMarks(m => ({ ...m, [key]: prev }));
      toast.error('No se pudo guardar');
    }
  };

  const colorLabel = (k) => (COLORS.find(c => c.key === k) || {}).label || 'Sin marcar';
  const prefName = (id) => (prefectos.find(p => p.id === id) || {}).name || '';

  const openNotice = (row, col) => {
    const key = `${row}-${col}`;
    setSelCell(null);
    setNoticeCell({ row, col });
    setNoticeTo(markedBy[key] || (prefectos[0] && prefectos[0].id) || '');
    setNoticeMsg('');
  };

  const handleSendNotice = async () => {
    if (!noticeCell || !noticeTo || !noticeMsg.trim() || sendingNotice) return;
    setSendingNotice(true);
    try {
      await axios.post(`${API}/schedule-notices`, {
        group_id: groupId, week_start: weekStart,
        row: noticeCell.row, col: noticeCell.col,
        to_prefecto_id: noticeTo, message: noticeMsg.trim(),
      }, authHeaders());
      toast.success('Aviso enviado a Prefectura');
      setNoticeCell(null); setNoticeMsg('');
      loadSentNotices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar el aviso');
    } finally {
      setSendingNotice(false);
    }
  };

  const token = localStorage.getItem('token');
  const imgUrl = sched?.has_image ? `${API}/schedules/${groupId}/image?auth=${token}&v=${encodeURIComponent(sched.version || '')}` : null;

  const doExport = async () => {
    if (!imgUrl || exporting) return;
    setExporting(true);
    let url = null;
    try {
      const res = await axios.get(imgUrl, { responseType: 'blob' });
      url = URL.createObjectURL(res.data);
      setBlobUrl(url);
      await new Promise(r => setTimeout(r, 120));
      const node = exportRef.current;
      const img = node?.querySelector('img');
      if (img && !img.complete) {
        await new Promise(r => { img.onload = r; img.onerror = r; });
      }
      await new Promise(r => setTimeout(r, 60));
      const canvas = await html2canvas(node, { backgroundColor: '#ffffff', scale: 2, useCORS: true, logging: false });
      const link = document.createElement('a');
      const safeName = (groupName || 'grupo').replace(/[^\w\-]+/g, '_');
      link.download = `horario_${safeName}_${weekStart || ''}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Horario descargado');
    } catch (err) {
      toast.error('No se pudo exportar el horario');
    } finally {
      if (url) URL.revokeObjectURL(url);
      setBlobUrl(null);
      setExporting(false);
    }
  };

  const handleDeleteImage = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/schedules/${groupId}`, authHeaders());
      toast.success('Imagen del horario eliminada');
      setConfirmDelete(false);
      setGroups(gs => gs.map(g => g.id === groupId ? { ...g, has_schedule: false } : g));
      selectGroup(groupId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={embedded ? '' : 'max-w-5xl mx-auto p-4'} data-testid="horarios-page">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Grupo</label>
          <select data-testid="horario-group-select" value={groupId} onChange={(e) => selectGroup(e.target.value)}
            className="h-10 rounded-lg border border-slate-200 px-3 min-w-[220px] bg-white">
            <option value="">Selecciona un grupo…</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}{g.has_schedule ? ' ✓' : ''}</option>)}
          </select>
        </div>
        {!canMark && weeks.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1"><Calendar className="w-4 h-4 inline mr-1" />Semana</label>
            <select data-testid="horario-week-select" value={weekStart || ''} onChange={(e) => loadMarks(groupId, e.target.value)}
              className="h-10 rounded-lg border border-slate-200 px-3 bg-white">
              {weeks.map(w => <option key={w} value={w}>Semana del {w}</option>)}
            </select>
          </div>
        )}
        {(canSend || (canExport && groupId && sched?.has_image)) && (
          <div className="ml-auto flex items-center gap-2">
            {canSend && (
              <Button variant="outline" data-testid="horario-sent-toggle"
                onClick={() => { setShowSent(s => !s); loadSentNotices(); }}
                className="h-10 text-sm">
                <MessageSquare className="w-4 h-4 mr-2" /> Avisos enviados ({sentNotices.length})
              </Button>
            )}
            {canExport && groupId && sched?.has_image && (
              <Button data-testid="horario-export-btn" onClick={doExport} disabled={exporting}
                className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white">
                <Download className="w-4 h-4 mr-2" /> {exporting ? 'Generando…' : 'Descargar PNG'}
              </Button>
            )}
          </div>
        )}
      </div>

      {canSend && showSent && (
        <Card className="p-3 mb-4" data-testid="horario-sent-list">
          <p className="text-sm font-semibold text-slate-700 mb-2">Avisos enviados a Prefectura</p>
          {sentNotices.length === 0 ? (
            <p className="text-xs text-slate-500">Aún no has enviado avisos.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-auto">
              {sentNotices.map(n => (
                <div key={n.id} className="border border-slate-100 rounded-lg p-2 text-xs" data-testid={`horario-sent-item-${n.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${n.status === 'attended' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {n.status === 'attended' ? 'Atendido' : 'Activo'}
                    </span>
                    <span className="font-medium text-slate-700">{n.group_name}</span>
                    <span className="text-slate-400">· Para {n.to_prefecto_name}</span>
                  </div>
                  <p className="text-slate-600 mt-1">{n.message}</p>
                  <p className="text-slate-400 mt-0.5">{n.row != null ? `Hora ${n.row + 1}, Día ${n.col + 1} · ` : ''}Semana {n.week_start}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {canUpload && groupId && (
        <Card className="p-4 mb-4 bg-slate-50 border-slate-200" data-testid="horario-upload-card">
          <p className="text-sm font-medium text-slate-700 mb-2">Subir/actualizar imagen del horario</p>
          <div className="flex flex-wrap items-end gap-3">
            <div><label className="block text-xs text-slate-500">Filas (horas)</label>
              <input type="number" min="1" max="30" value={rows} onChange={e => setRows(parseInt(e.target.value) || 1)} data-testid="horario-rows-input" className="h-9 w-24 rounded border border-slate-200 px-2" /></div>
            <div><label className="block text-xs text-slate-500">Columnas (días)</label>
              <input type="number" min="1" max="15" value={cols} onChange={e => setCols(parseInt(e.target.value) || 1)} data-testid="horario-cols-input" className="h-9 w-24 rounded border border-slate-200 px-2" /></div>
            <label className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium cursor-pointer" data-testid="horario-upload-btn">
              <Upload className="w-4 h-4" /> {uploading ? 'Subiendo…' : 'Elegir imagen'}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleUpload} disabled={uploading} />
            </label>
            {sched?.has_image && !confirmDelete && (
              <Button data-testid="horario-delete-btn" onClick={() => setConfirmDelete(true)}
                className="h-9 bg-red-600 hover:bg-red-700 text-white text-sm">
                <Trash2 className="w-4 h-4 mr-1.5" /> Eliminar imagen
              </Button>
            )}
          </div>
          {sched?.has_image && confirmDelete && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3" data-testid="horario-delete-confirm">
              <p className="text-sm text-red-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>¿Eliminar la imagen del horario de este grupo? Se borrarán también las marcas de cumplimiento (colores) de todas las semanas. Esta acción no se puede deshacer.</span>
              </p>
              <div className="flex gap-2 mt-3">
                <Button data-testid="horario-delete-confirm-btn" onClick={handleDeleteImage} disabled={deleting}
                  className="h-9 bg-red-600 hover:bg-red-700 text-white text-sm">
                  {deleting ? 'Eliminando…' : 'Sí, eliminar'}
                </Button>
                <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={deleting} className="h-9 text-sm">
                  Cancelar
                </Button>
              </div>
            </div>
          )}
          <p className="text-xs text-slate-400 mt-2">Define filas y columnas para que la rejilla táctil calce con tu horario.</p>
        </Card>
      )}

      {/* Leyenda */}
      {groupId && sched?.has_image && (
        <div className="flex flex-wrap gap-3 mb-3 text-xs" data-testid="horario-legend">
          {COLORS.map(c => (<span key={c.key} className="inline-flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded" style={{ background: c.dot }} />{c.label}</span>))}
        </div>
      )}

      {groupId && sched && !sched.has_image && (
        <div className="text-slate-500 text-sm p-6 text-center border border-dashed border-slate-200 rounded-lg" data-testid="horario-empty">
          {canUpload ? 'Este grupo aún no tiene horario. Súbelo arriba.' : 'Este grupo aún no tiene horario cargado.'}
        </div>
      )}

      {/* Imagen + rejilla */}
      {groupId && sched?.has_image && (
        <div className="relative inline-block w-full border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid="horario-grid-wrap">
          <img src={imgUrl} alt="Horario" className="block w-full select-none" draggable={false} />
          <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${cols},1fr)`, gridTemplateRows: `repeat(${rows},1fr)` }}>
            {Array.from({ length: rows * cols }).map((_, i) => {
              const r = Math.floor(i / cols); const c = i % cols;
              const key = `${r}-${c}`;
              return (
                <div key={key} data-testid={`horario-cell-${r}-${c}`}
                  onClick={() => { if (canMark) setSelCell({ row: r, col: c }); else if (canSend) openNotice(r, c); }}
                  className={`border border-slate-300/40 ${(canMark || canSend) ? 'cursor-pointer hover:ring-2 hover:ring-indigo-400' : ''}`}
                  style={{ background: colorCss(marks[key]) }} />
              );
            })}
            {showToday && (
              <div data-testid="horario-today-badge" className="pointer-events-none absolute top-0"
                style={{ left: `${(todayCol / cols) * 100}%`, width: `${100 / cols}%`, zIndex: 6 }}>
                <span className="mx-auto mt-1 block w-fit px-2 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold shadow">HOY</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Paleta de colores (prefectura) — aparece justo debajo del horario */}
      {canMark && selCell && (
        <div ref={paletteRef} className="mt-3 bg-white rounded-2xl shadow-lg border border-indigo-200 ring-2 ring-indigo-100 p-3 flex flex-wrap items-center gap-2" data-testid="horario-color-palette">
          <span className="text-xs font-medium text-slate-600 mr-1 w-full sm:w-auto">Hora {selCell.row + 1}, Día {selCell.col + 1}:</span>
          {COLORS.map(c => (
            <button key={c.key} data-testid={`horario-pick-${c.key}`} onClick={() => applyColor(c.key)}
              className="inline-flex items-center gap-1.5 h-9 pl-1.5 pr-3 rounded-full border border-slate-200 hover:bg-slate-50 transition-colors" title={c.label}>
              <span className="w-6 h-6 rounded-full border-2 border-white shadow" style={{ background: c.dot }} />
              <span className="text-xs text-slate-600 hidden sm:inline">{c.label}</span>
            </button>
          ))}
          <button data-testid="horario-pick-clear" onClick={() => applyColor(null)} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-slate-300 text-slate-500 hover:bg-slate-50" title="Limpiar"><X className="w-4 h-4" /> <span className="text-xs hidden sm:inline">Limpiar</span></button>
          <button onClick={() => setSelCell(null)} className="text-slate-400 ml-auto text-sm px-2">Cerrar</button>
        </div>
      )}

      {canSend && sched?.has_image && !noticeCell && (
        <p className="text-xs text-slate-500 mt-2" data-testid="horario-sender-hint">Toca una casilla del horario para enviar un aviso a Prefectura sobre esa clase.</p>
      )}

      {/* Composer de aviso (subdirección / jefe de docentes) — debajo del horario */}
      {canSend && noticeCell && (
        <div ref={noticeRef} className="mt-3 bg-white rounded-2xl shadow-lg border border-amber-200 ring-2 ring-amber-100 p-4" data-testid="horario-notice-composer">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-slate-700">Enviar aviso a Prefectura</span>
            <button onClick={() => setNoticeCell(null)} className="ml-auto text-slate-400 text-sm">Cerrar</button>
          </div>
          <div className="text-xs text-slate-500 mb-3">
            Clase: <span className="font-medium text-slate-700">Hora {noticeCell.row + 1}, Día {noticeCell.col + 1}</span>
            {' · '}Estado actual:
            <span className="inline-flex items-center gap-1 ml-1 align-middle">
              <span className="w-3 h-3 rounded-full inline-block" style={{ background: (COLORS.find(c => c.key === marks[`${noticeCell.row}-${noticeCell.col}`]) || {}).dot || '#e2e8f0' }} />
              <span className="font-medium text-slate-700">{colorLabel(marks[`${noticeCell.row}-${noticeCell.col}`])}</span>
            </span>
            {markedBy[`${noticeCell.row}-${noticeCell.col}`] && (
              <span className="block mt-1">Coloreado por: <span className="font-medium text-slate-700">{prefName(markedBy[`${noticeCell.row}-${noticeCell.col}`]) || 'Prefecto'}</span></span>
            )}
          </div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Enviar a</label>
          <select data-testid="horario-notice-to" value={noticeTo} onChange={e => setNoticeTo(e.target.value)}
            className="h-9 w-full rounded-lg border border-slate-200 px-3 bg-white mb-3 text-sm">
            <option value="">Selecciona un prefecto…</option>
            {prefectos.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <label className="block text-xs font-medium text-slate-600 mb-1">Mensaje</label>
          <textarea data-testid="horario-notice-message" value={noticeMsg} onChange={e => setNoticeMsg(e.target.value)}
            rows={3} placeholder="Ej. El profesor sí estaba justificado (comisión). Favor de corregir el color."
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mb-3 resize-none" />
          <div className="flex gap-2">
            <Button data-testid="horario-notice-send" onClick={handleSendNotice} disabled={sendingNotice || !noticeTo || !noticeMsg.trim()}
              className="h-9 bg-amber-600 hover:bg-amber-700 text-white text-sm">
              <Send className="w-4 h-4 mr-1.5" /> {sendingNotice ? 'Enviando…' : 'Enviar aviso'}
            </Button>
            <Button variant="outline" onClick={() => setNoticeCell(null)} disabled={sendingNotice} className="h-9 text-sm">Cancelar</Button>
          </div>
        </div>
      )}

      {/* Nodo oculto para exportar a PNG */}
      {blobUrl && (
        <div style={{ position: 'fixed', left: '-10000px', top: 0 }} aria-hidden="true">
          <div ref={exportRef} data-testid="horario-export-node" style={{ width: '820px', background: '#ffffff', padding: '24px', fontFamily: 'Arial, sans-serif', boxSizing: 'border-box' }}>
            <div style={{ marginBottom: '14px', borderBottom: '2px solid #e2e8f0', paddingBottom: '10px' }}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>Horario · {groupName || 'Grupo'}</div>
              <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>Semana del {weekStart || '—'} · Cumplimiento de horarios</div>
            </div>
            <div style={{ position: 'relative', display: 'block', width: '100%', border: '1px solid #e2e8f0' }}>
              <img src={blobUrl} alt="Horario" style={{ display: 'block', width: '100%' }} />
              <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: `repeat(${cols},1fr)`, gridTemplateRows: `repeat(${rows},1fr)` }}>
                {Array.from({ length: rows * cols }).map((_, i) => {
                  const r = Math.floor(i / cols); const c = i % cols;
                  return <div key={`${r}-${c}`} style={{ border: '1px solid rgba(203,213,225,0.4)', background: colorCss(marks[`${r}-${c}`]) }} />;
                })}
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '14px' }}>
              {COLORS.map(c => (
                <span key={c.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#334155' }}>
                  <span style={{ width: '14px', height: '14px', borderRadius: '3px', background: c.dot, display: 'inline-block' }} />{c.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HorariosPage;
